import {
  Braces,
  Eye,
  FileText,
  Globe,
  ImageIcon,
  Lightbulb,
  Mic,
  Table as TableIcon,
  Video,
  Wrench,
} from "lucide-react";

/**
 * Capabilities that the model picker filters out by default because they're
 * implicit / supported by virtually every model.
 */
export const HIDDEN_CAPABILITIES = new Set([
  "streaming",
  "system_prompt",
  "json_mode",
  "tools",
  "parallel_function_calling",
]);

export const CAPABILITY_BADGE_MAP: Record<
  string,
  { label: string; icon?: React.ElementType }
> = {
  vision: { label: "Vision", icon: Eye },
  function_calling: { label: "Function calling", icon: Wrench },
  web_search: { label: "Web search", icon: Globe },
  reasoning: { label: "Reasoning", icon: Lightbulb },
  structured_output: { label: "Structured output", icon: Braces },
  document_input: { label: "Document input", icon: FileText },
  spreadsheet_input: { label: "Spreadsheet input", icon: TableIcon },
  audio_in: { label: "Audio input", icon: Mic },
  video_in: { label: "Video input", icon: Video },
  image_generation: { label: "Image generation", icon: ImageIcon },
};

const CAPABILITY_PRIORITY = ["function_calling", "reasoning", "vision"];

export const getCapabilityLabel = (cap: string): string =>
  CAPABILITY_BADGE_MAP[cap]?.label ??
  cap
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

export const sortCapabilities = (
  caps: string[],
  { hideImplicit = true }: { hideImplicit?: boolean } = {}
): string[] =>
  [...caps]
    .filter((cap) => !hideImplicit || !HIDDEN_CAPABILITIES.has(cap))
    .sort((a, b) => {
      const aPriority = CAPABILITY_PRIORITY.indexOf(a);
      const bPriority = CAPABILITY_PRIORITY.indexOf(b);
      if (aPriority !== -1 || bPriority !== -1) {
        return (
          (aPriority === -1 ? Infinity : aPriority) -
          (bPriority === -1 ? Infinity : bPriority)
        );
      }
      const aHasIcon = !!CAPABILITY_BADGE_MAP[a]?.icon;
      const bHasIcon = !!CAPABILITY_BADGE_MAP[b]?.icon;
      return (bHasIcon ? 1 : 0) - (aHasIcon ? 1 : 0);
    });

export const CapabilityChip = ({ cap }: { cap: string }) => {
  const Icon = CAPABILITY_BADGE_MAP[cap]?.icon;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-border bg-muted/40 font-medium">
      {Icon && (
        <Icon size={11} strokeWidth={1.5} className="text-muted-foreground" />
      )}
      {getCapabilityLabel(cap)}
    </span>
  );
};
